import { useCookie, useRuntimeConfig } from '#imports'
import { computed, reactive, ref, watch } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { DIDOMI_COOKIE_NAME, VENDORS_ID } from '../didomi/config'
import { isClient } from '../utils/isClient'

export function useDataConsent() {
  const { market } = useMarketplace()
  const {
    ENABLE_APPSFLYER_WITH_DIDOMI_COUNTRIES,
    tracking: { didomi, countriesWithNoCookieOptIn },
  } = useRuntimeConfig().public
  const cookie = useCookie<string | null>(DIDOMI_COOKIE_NAME)
  const logger = useLogger()

  const isDidomiEnabled =
    didomi.isEnabled && didomi.enabledCountryCodes.includes(market.countryCode)
  const isAppsFlyerWithDidomiEnabled =
    ENABLE_APPSFLYER_WITH_DIDOMI_COUNTRIES?.includes(market.countryCode)

  const vendorAcceptanceStates = reactive<
    Record<keyof typeof VENDORS_ID, boolean>
  >({
    amplitude: false,
    braze: false,
    gtm: false,
    appsFlyer: false,
  })

  // Helper function to determine if modal should be displayed based on country and cookie state
  // This logic is used both for initial SSR state and client-side updates
  const shouldDisplayModal = (hasConsentCookie: boolean) => {
    return (
      !countriesWithNoCookieOptIn.includes(market.countryCode) &&
      !hasConsentCookie
    )
  }

  // Server-side modal display logic: Show modal if country requires cookie consent AND no consent cookie exists
  // This determines the initial SSR state - the modal will be hidden client-side if user has already consented
  const shouldShowModalServerSide = shouldDisplayModal(!!cookie.value)

  const isCookiesModalDisplayed = ref(shouldShowModalServerSide)

  // Client-side watcher: Update modal visibility when consent cookie changes
  // This handles the case where user accepts/declines cookies after page load
  watch(
    cookie,
    () => {
      isCookiesModalDisplayed.value = shouldDisplayModal(!!cookie.value)
    },
    { immediate: false },
  )

  function showCookiesModal() {
    if (!isClient()) return

    window.Didomi?.preferences.show()
  }

  function acceptAll() {
    if (!isClient()) return

    window.Didomi?.setUserAgreeToAll()
  }

  function declineAll() {
    if (!isClient()) return

    window.Didomi?.setUserDisagreeToAll()
  }

  function getUserConsentForVendor(
    vendor: (typeof VENDORS_ID)[keyof typeof VENDORS_ID],
  ): boolean {
    try {
      if (!isDidomiEnabled || !isClient() || !window.Didomi) return false

      return window.Didomi.getCurrentUserStatus().vendors[vendor].enabled
    } catch (error) {
      logger.error(`[Didomi] Error getting user consent for vendor ${vendor}`, {
        error: error as Error,
        owners: ['bot-group-onboard-front'],
      })

      return false
    }
  }

  function getUserConsentForAmplitude() {
    return getUserConsentForVendor(VENDORS_ID.amplitude)
  }

  function updateVendorAcceptanceStates() {
    for (const [vendorKey, vendorId] of Object.entries(VENDORS_ID)) {
      const vendor = vendorKey as keyof typeof VENDORS_ID
      vendorAcceptanceStates[vendor] = getUserConsentForVendor(vendorId)
    }
  }

  const isAppsFlyerUserConsentGiven = computed(() => {
    if (!window?.Didomi) return false

    return vendorAcceptanceStates.appsFlyer
  })

  if (
    isDidomiEnabled &&
    isAppsFlyerWithDidomiEnabled &&
    isClient() &&
    window?.Didomi
  ) {
    updateVendorAcceptanceStates()

    // consent can be changed by the user at any time during app navigation
    window.Didomi.on('consent.changed', () => {
      updateVendorAcceptanceStates()
    })
  }

  return {
    acceptAll,
    declineAll,
    showCookiesModal,
    getUserConsentForAmplitude,
    isAppsFlyerUserConsentGiven,
    isDidomiEnabled,
    isCookiesModalDisplayed,
  }
}
